import { useCallback, useEffect, useMemo, useState } from 'react'

export interface IDisplaySettings {
  // Look and Feel
  isNewLookAndFeel: boolean
  isYearlyPlanning: boolean
  isShowDetailedPlanning: boolean

  // Previous Plans & Achievements
  isShowPreviousPlans: boolean
  isShowPlanAchievement: boolean

  // Quota
  isShowQuotaTargets: boolean
  isShowQuotaAchievement: boolean

  // Actuals Split: Direct/Indirect
  isShowActualsSplit: boolean
  isShowSmbVsEuSplit: boolean

  // YoY (Year-over-Year)
  isShowYoYActualsVsActuals: boolean
  isShowYoYActualsVsPlans: boolean
  isShowYoYPlansVsPlans: boolean
  isShowYoYQuotaVsActuals: boolean
  isShowYoYPartnersVsPartners: boolean

  // QoQ (Quarter-over-Quarter)
  isShowQoQActualsVsActuals: boolean
  isShowQoQActualsVsPlans: boolean
  isShowQoQPlansVsPlans: boolean
  isShowQoQQuotaVsActuals: boolean
  isShowQoQPartnersVsPartners: boolean

  // Other
  isShowPartnerCount: boolean
}

export interface IDisplaySettingsActions {
  setIsNewLookAndFeel: (value: boolean) => void
  setIsYearlyPlanning: (value: boolean) => void
  setIsShowDetailedPlanning: (value: boolean) => void

  setIsShowPreviousPlans: (value: boolean) => void
  setIsShowPlanAchievement: (value: boolean) => void
  setIsShowQuotaTargets: (value: boolean) => void
  setIsShowQuotaAchievement: (value: boolean) => void
  setIsShowActualsSplit: (value: boolean) => void
  setIsShowSmbVsEuSplit: (value: boolean) => void
  setIsShowYoYActualsVsActuals: (value: boolean) => void
  setIsShowYoYActualsVsPlans: (value: boolean) => void
  setIsShowYoYPlansVsPlans: (value: boolean) => void
  setIsShowYoYQuotaVsActuals: (value: boolean) => void
  setIsShowYoYPartnersVsPartners: (value: boolean) => void
  setIsShowQoQActualsVsActuals: (value: boolean) => void
  setIsShowQoQActualsVsPlans: (value: boolean) => void
  setIsShowQoQPlansVsPlans: (value: boolean) => void
  setIsShowQoQQuotaVsActuals: (value: boolean) => void
  setIsShowQoQPartnersVsPartners: (value: boolean) => void
  setIsShowPartnerCount: (value: boolean) => void

  // Bulk actions
  resetToDefaults: () => void
  updateSettings: (settings: Partial<IDisplaySettings>) => void
}

export interface IDisplaySettingsConfig {
  programType?: string
  isConsolidated?: boolean
  isRetailer?: boolean
  // Actuals Split (Direct/Indirect) and SMB vs EU split are not available for distributors.
  isDistributor?: boolean
}

const getDefaultSettings = (
  programType?: string,
  isConsolidated?: boolean,
  isRetailer?: boolean
): IDisplaySettings => {
  const isConsolidatedMode = programType === 'Consolidated' || isConsolidated

  return {
    isNewLookAndFeel: true,
    isYearlyPlanning: !!isRetailer,
    isShowDetailedPlanning: false,

    isShowPreviousPlans: true,
    isShowPlanAchievement: !isConsolidatedMode && !isRetailer,
    isShowQuotaTargets: false,
    isShowQuotaAchievement: false,
    isShowActualsSplit: false,
    isShowSmbVsEuSplit: false,
    isShowYoYActualsVsActuals: !isConsolidatedMode && !isRetailer,
    isShowYoYActualsVsPlans: !isConsolidatedMode,
    isShowYoYPlansVsPlans: false,
    isShowYoYQuotaVsActuals: false,
    isShowYoYPartnersVsPartners: false,
    isShowQoQActualsVsActuals: false,
    isShowQoQActualsVsPlans: false,
    isShowQoQPlansVsPlans: false,
    isShowQoQQuotaVsActuals: false,
    isShowQoQPartnersVsPartners: false,
    isShowPartnerCount: !isConsolidatedMode && !isRetailer,
  }
}

export const useDisplaySettings = (config?: IDisplaySettingsConfig) => {
  const defaultSettings = useMemo(
    () => getDefaultSettings(config?.programType, config?.isConsolidated, config?.isRetailer),
    [config?.programType, config?.isConsolidated, config?.isRetailer]
  )

  const [settings, setSettings] = useState<IDisplaySettings>(defaultSettings)

  // Individual setters
  const setIsNewLookAndFeel = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isNewLookAndFeel: value }))
  }, [])

  const setIsYearlyPlanning = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isYearlyPlanning: value }))
  }, [])

  const setIsShowDetailedPlanning = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowDetailedPlanning: value }))
  }, [])

  const setIsShowPreviousPlans = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowPreviousPlans: value }))
  }, [])

  const setIsShowPlanAchievement = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowPlanAchievement: value }))
  }, [])

  const setIsShowQuotaTargets = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowQuotaTargets: value }))
  }, [])

  const setIsShowQuotaAchievement = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowQuotaAchievement: value }))
  }, [])

  const setIsShowActualsSplit = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      isShowActualsSplit: value,
      isShowSmbVsEuSplit: value ? false : prev.isShowSmbVsEuSplit,
    }))
  }, [])

  const setIsShowSmbVsEuSplit = useCallback((value: boolean) => {
    setSettings((prev) => ({
      ...prev,
      isShowActualsSplit: value ? false : prev.isShowActualsSplit,
      isShowSmbVsEuSplit: value,
    }))
  }, [])

  const setIsShowYoYActualsVsActuals = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowYoYActualsVsActuals: value }))
  }, [])

  const setIsShowYoYActualsVsPlans = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowYoYActualsVsPlans: value }))
  }, [])

  const setIsShowYoYPlansVsPlans = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowYoYPlansVsPlans: value }))
  }, [])

  const setIsShowYoYQuotaVsActuals = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowYoYQuotaVsActuals: value }))
  }, [])

  const setIsShowYoYPartnersVsPartners = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowYoYPartnersVsPartners: value }))
  }, [])

  const setIsShowQoQActualsVsActuals = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowQoQActualsVsActuals: value }))
  }, [])

  const setIsShowQoQActualsVsPlans = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowQoQActualsVsPlans: value }))
  }, [])

  const setIsShowQoQPlansVsPlans = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowQoQPlansVsPlans: value }))
  }, [])

  const setIsShowQoQQuotaVsActuals = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowQoQQuotaVsActuals: value }))
  }, [])

  const setIsShowQoQPartnersVsPartners = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowQoQPartnersVsPartners: value }))
  }, [])

  const setIsShowPartnerCount = useCallback((value: boolean) => {
    setSettings((prev) => ({ ...prev, isShowPartnerCount: value }))
  }, [])

  // Bulk actions
  const resetToDefaults = useCallback(() => {
    setSettings(getDefaultSettings(config?.programType, config?.isConsolidated, config?.isRetailer))
  }, [config?.programType, config?.isConsolidated, config?.isRetailer])

  const updateSettings = useCallback((newSettings: Partial<IDisplaySettings>) => {
    setSettings((prev) => {
      const nextSettings = { ...prev, ...newSettings }

      if (newSettings.isShowActualsSplit) {
        nextSettings.isShowSmbVsEuSplit = false
      }

      if (newSettings.isShowSmbVsEuSplit) {
        nextSettings.isShowActualsSplit = false
      }

      return nextSettings
    })
  }, [])
  const actions: IDisplaySettingsActions = useMemo(
    () => ({
      setIsNewLookAndFeel,
      setIsYearlyPlanning,
      setIsShowDetailedPlanning,

      setIsShowPreviousPlans,
      setIsShowPlanAchievement,
      setIsShowQuotaTargets,
      setIsShowQuotaAchievement,
      setIsShowActualsSplit,
      setIsShowSmbVsEuSplit,
      setIsShowYoYActualsVsActuals,
      setIsShowYoYActualsVsPlans,
      setIsShowYoYPlansVsPlans,
      setIsShowYoYQuotaVsActuals,
      setIsShowYoYPartnersVsPartners,
      setIsShowQoQActualsVsActuals,
      setIsShowQoQActualsVsPlans,
      setIsShowQoQPlansVsPlans,
      setIsShowQoQQuotaVsActuals,
      setIsShowQoQPartnersVsPartners,
      setIsShowPartnerCount,

      resetToDefaults,
      updateSettings,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  useEffect(() => {
    if (settings.isYearlyPlanning) {
      updateSettings({
        isShowQoQActualsVsActuals: false,
        isShowQoQActualsVsPlans: false,
        isShowQoQPartnersVsPartners: false,
        isShowQoQPlansVsPlans: false,
        isShowQoQQuotaVsActuals: false,
      })
    }
  }, [settings.isYearlyPlanning, updateSettings])

  // Split controls are hidden for distributors, so their settings must never
  // reach the consumers there - otherwise a value left over from another partner type
  // would keep rendering Direct/Indirect or SMB/EU rows nobody can switch off.
  const effectiveSettings = useMemo(
    () =>
      config?.isDistributor && (settings.isShowActualsSplit || settings.isShowSmbVsEuSplit)
        ? { ...settings, isShowActualsSplit: false, isShowSmbVsEuSplit: false }
        : settings,
    [settings, config?.isDistributor]
  )

  return useMemo(
    () => ({
      settings: effectiveSettings,
      actions,
    }),
    [effectiveSettings, actions]
  )
}
